import React, { useEffect } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Button,
  Input,
  Listbox,
  Option,
} from "@fluentui/react-components";
import { FaCaretDown } from "react-icons/fa";
import { ResourceSpaceType } from "../APIService/ResourceSpaceTypeListService";
import { IoMdCheckmark } from "react-icons/io";
import { IoCheckmarkCircleSharp } from "react-icons/io5";
import { Equipment } from '../APIService/SearchService';
import { IoIosRadioButtonOff } from "react-icons/io";
import { FaRegCircleCheck } from "react-icons/fa6";

export type SelectedOption<T> = {
  id: string;
  label: string;
  index: number;
  item: T;
};

type CommonProps<T> = {
  data: T[];
  placeholder: string;

  getId: (item: T) => string;
  getLabel: (item: T) => string;
  getIcon?: (item: T) => string | undefined;

  showCount?: boolean;
  enableSearch?: boolean;
};

type SingleSelectProps<T> = CommonProps<T> & {
  multiselect?: false;
  onChange?: (selected: SelectedOption<T>) => void;
  defaultValue?: T;
};

type MultiSelectProps<T> = CommonProps<T> & {
  multiselect: true;
  onChange?: (selected: SelectedOption<T>[]) => void;
  defaultValue?: T[];
};

type SelectDropDownProps<T> =
  | SingleSelectProps<T>
  | MultiSelectProps<T>;

const SelectDropDown = <T,>(props: SelectDropDownProps<T>) => {
  const {
    data,
    placeholder,
    getId,
    getLabel,
    getIcon,
    showCount = true,
    enableSearch = true,
    multiselect = false,
    onChange,
  } = props;

  const [selected, setSelected] = React.useState<string[]>(() => {
    if (multiselect) {
      return (props as MultiSelectProps<T>).defaultValue?.map(getId) || [];
    } else {
      return (props as SingleSelectProps<T>).defaultValue ? [getId((props as SingleSelectProps<T>).defaultValue!)] : [];
    }
  });
  const [search, setSearch] = React.useState("");

  const filteredData = enableSearch
    ? data.filter(item =>
      getLabel(item).toLowerCase().includes(search.toLowerCase())
    )
    : data;

  const handleOptionSelect = (
    _event: unknown,
    selectionData: { selectedOptions: string[] }
  ) => {
    const selectedIds = selectionData.selectedOptions;
    setSelected(selectedIds);

    // Narrow by multiselect
    if (props.multiselect) {
      // multi-select: onChange expects an array
      if (onChange) {
        const selectedItems: SelectedOption<T>[] = selectedIds.map(id => {
          const index = data.findIndex(item => getId(item) === id);
          const item = data[index];
          return { id, label: getLabel(item), index, item };
        });
        props.onChange?.(selectedItems);
      }
    } else {
      // single-select: onChange expects a single item
      if (onChange && selectedIds.length > 0) {
        const id = selectedIds[0];
        const index = data.findIndex(item => getId(item) === id);
        const item = data[index];
        if (!item) return;

        const selectedItem: SelectedOption<T> = {
          id,
          label: getLabel(item),
          index,
          item,
        };

        props.onChange?.(selectedItem);
      }
    }
  };


  const selectedLabel =
    !multiselect && selected.length > 0
      ? getLabel(data.find(d => getId(d) === selected[0])!)
      : placeholder;

  const isChecked = (id: string) => selected.includes(id);

  return (
    <Popover positioning="below-start">
      <PopoverTrigger>
        <Button appearance="outline" shape="circular" size="small">
          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {multiselect ? placeholder : selectedLabel}

            {multiselect && showCount && selected.length > 0 && (
              <span
                style={{
                  backgroundColor: "#3C30FF",
                  borderRadius: 20,
                  padding: "0 6px",
                  color: "white",
                }}
              >
                {selected.length}
              </span>
            )}
          </span>
          <FaCaretDown />
        </Button>
      </PopoverTrigger>

      <PopoverSurface style={{ padding: 0 }}>
        {enableSearch && (
          <Input
            placeholder={`Search ${placeholder}`}
            appearance="underline"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 250 }}
          />
        )}

        <Listbox
          multiselect={multiselect}
          selectedOptions={selected}
          onOptionSelect={handleOptionSelect}
          style={{ maxHeight: 200, overflowY: "auto", width: 250 }}
        >
          {filteredData.map(item => (
            <Option
              key={getId(item)}
              value={getId(item)}
              text={getLabel(item)}
              checkIcon={!multiselect ? null : undefined}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: !multiselect ? "20px 1fr 20px" : "auto 1fr auto",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  width: "100%"
                }}
              >
                {!multiselect && (
                  <span style={{ display: "flex", justifyContent: "center" }}>
                    {isChecked(getId(item)) ? (
                      <FaRegCircleCheck size={14} />
                    ) : (
                      <IoIosRadioButtonOff size={14} />
                    )}
                  </span>
                )}

                <span style={{ whiteSpace: "nowrap" }}>
                  {getLabel(item)}
                </span>

                {getIcon?.(item) && (
                  <span style={{ display: "flex", justifyContent: "flex-end" }}>
                    <img
                      src={`${localStorage.getItem("tenantUrl")}${getIcon(item)}`}
                      style={{ width: 15, height: 15 }}
                      alt=""
                    />
                  </span>
                )}
              </div>
            </Option>
          ))}
        </Listbox>
      </PopoverSurface>
    </Popover>
  );
};

export default SelectDropDown;